import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { isDemoLimitErrorCode } from 'src/enum/codeErreur';
import { StatutLimiteDemoModel } from 'src/models/pilotage/statutLimiteDemoModel';
import { firstValueFrom, forkJoin, interval, Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as tus from 'tus-js-client';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { AnswerModel, QuestionModel } from 'src/models/pilotage/questionModel';
import { QuestionTypeEnum } from 'src/models/pilotage/questionTypeEnum';
import { CreateScormLanguageInfoComponent } from './create-scorm-language-info/create-scorm-language-info.component';
import { EditVideoLanguageModalComponent, EditVideoLanguageModalData } from './edit-video-language-modal/edit-video-language-modal.component';
import { PackageNameDialogComponent } from './package-name-dialog/package-name-dialog.component';
import { RestoreDraftDialogComponent, RestoreDraftDialogData } from './restore-draft-dialog/restore-draft-dialog.component';
import { PackagePersoDraft, PackagePersoDraftService } from 'src/services/package-perso-draft.service';
import { PackagePersoDraftState } from 'src/models/pilotage/packagePersoDraftState';
import { AttachedDocument, PackagePersoPilotageModel, VideoAssociation, QuizConclusionModel } from 'src/models/pilotage/packagePersoPilotageModel';
import {
  PackagesPersoService,
  CreatePackageResponse,
  GetQuizInfosFromJsonResponse,
  LanguageDto,
  QuizOutroMessageTextDto,
  QuizQuestionAnswerDto,
  QuizQuestionDto,
} from 'src/services/packages-perso.service';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { ComponentCanDeactivate } from 'src/services/unsaved-changes.guard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ContexteUtilisateur, EntiteModel } from 'src/models/pilotage/contextePilotageUtilisateurModel';
import { ActivatedRoute, Router } from '@angular/router';
import { PackagePersoEditModel } from 'src/models/pilotage/packagePersoEditModel';
import { ConfirmationTogglePackageDialogComponent } from '../confirmation-toggle-package-dialog/confirmation-toggle-package-dialog.component';
import { environment } from 'src/environments/environment';
import { NomEnvironment } from 'src/models/nom-environment';
import { VideoReadinessStatus } from 'src/models/videoStatusModel';

type LanguageCode = 'FR' | 'EN' | 'ES';

type QuizPlaceholderField =
  | 'introTitre'
  | 'introSousTitre'
  | 'echecTitre'
  | 'echecSousTitre'
  | 'reussiteTitre'
  | 'reussiteSousTitre';

type RawLanguageMap = Partial<Record<'fr' | 'en' | 'es', string>>;
interface RawHtmlContainer { html?: RawLanguageMap; }
interface RawOutroMessage {
  scoreMax?: number;
  title?: RawHtmlContainer;
  subtitle?: RawHtmlContainer;
  continueButton?: RawHtmlContainer;
}
interface RawQuestionAnswer {
  right?: boolean;
  html?: RawLanguageMap;
}
interface RawQuestion {
  html?: RawLanguageMap;
  answers?: Record<string, RawQuestionAnswer>;
  answersAreRandom?: boolean;
  images?: RawLanguageMap[];
}
interface RawQuestionnaireJson {
  title?: RawHtmlContainer;
  imagesRelativePath?: string;
  posterFrame?: RawLanguageMap;
  answersAreRandom?: boolean;
  autoValidateSingleSelection?: boolean;
  introMessage?: {
    title?: RawHtmlContainer;
    subtitle?: RawHtmlContainer;
  };
  outroMessages?: RawOutroMessage[];
  questions?: Record<string, RawQuestion>;
}

@Component({
    selector: 'app-create-package-perso',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatExpansionModule,
        MatRadioModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatNativeDateModule,
        MatTabsModule,
        MatSelectModule,
        CreateScormLanguageInfoComponent
    ],
    templateUrl: './create-package-perso.component.html',
    styleUrls: ['./create-package-perso.component.scss']
})
export class CreatePackagePersoComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
  protected readonly QuestionTypeEnum = QuestionTypeEnum;

  groupeId: number | undefined;

  package: PackagePersoPilotageModel = {
    nomPackage: '',
    scormVersion: '2004',
    nomFR: '',
    descriptionFR: '',
    nomEN: '',
    descriptionEN: '',
    nomES: '',
    descriptionES: '',
    contentType: 'pdf',
    pdfFileFR: null,
    pdfFileEN: null,
    pdfFileES: null,
    thumbnail: null,
    videos: [],
    subtitleFileFR: null,
    subtitleFileEN: null,
    subtitleFileES: null,
    canSeekForward: false,
    readingConfirmationRequired: false,
    questions: [],
    quizInfoGenerale: {
      titreFR: '',
      titreEN: '',
      titreES: '',
      posterFrameFR: '',
      posterFrameEN: '',
      posterFrameES: '',
      picturesRelativePath: '',
      isAnswersAreRandom: false,
      isAutoValidateSingleSelection: false,
    },
    quizIntroduction: {
      titreFR: '',
      titreEN: '',
      titreES: '',
      sousTitreFR: '',
      sousTitreEN: '',
      sousTitreES: '',
    },
    quizConclusions: [],
    entiteIds: [],
    groupeId: undefined,
    dureeEnMinutes: undefined,
    pourcentageSucces: undefined,
    donneCertificat: false,
    attachedDocuments: [],
  };

  selectedLanguages = {
    FR: true,
    EN: false,
    ES: false
  };

  uploadedVideos: VideoAssociation[] = [];
  currentVideoUpload: File | null = null;
  currentVimeoId: string | null = null;
  currentThumbnail: File | null = null;
  selectedLanguagesForVideo: { [key: string]: boolean } = {
    FR: false,
    EN: false,
    ES: false
  };

  entitesAccessibles: { id: number, nom: string }[] = [];
  entitesSelectionnees: { id: number, nom: string }[] = [];
  entitesDisponibles: { id: number, nom: string }[] = [];
  entiteIdSelectionnee: number | null = -1;
  nomPackage: string = '';
  scormVersion: string = '2004';
  estPourToutGroupe: boolean = false;
  estPiloteGroupe: boolean = false;

  estEnCoursDeEnregistrement = false;
  isVideoUploading = false;
  videoUploadProgress = 0;
  private pendingVideoUploads = 0;
  videoUploadProgressByLanguage: Record<LanguageCode, number> = { FR: 0, EN: 0, ES: 0 };
  videoUploadingByLanguage: Record<LanguageCode, boolean> = { FR: false, EN: false, ES: false };
  videoStatuses = new Map<string, VideoReadinessStatus>();
  private videoStatusPolls = new Map<string, Subscription>();
  isDraggingVideo = false;
  isDraggingThumbnail = false;
  vimeoIdError: string = '';
  private vimeoIdErrorsById = new Map<string, string | null>();
  private vimeoIdToValidate = new Subject<string>();
  private vimeoIdValidationSubscription: Subscription | null = null;
  videoFileError = '';
  thumbnailFileError = '';

  isDraggingDocument = false;
  attachedDocumentError = '';
  private readonly attachedDocumentAllowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
  private readonly attachedDocumentMaxSizeBytes = 50 * 1024 * 1024;
  private readonly attachedDocumentMaxCount = 5;

  readonly maxAnswersPerQuestion = 10;
  readonly minAnswersPerQuestion = 2;
  readonly minCorrectAnswersForMultipleChoice = 2;

  private readonly defaultScormVersion = '2004';
  private readonly minDurationInMinutes = 1;
  private readonly maxDurationInMinutes = 999;
  private readonly defaultDurationInMinutes = 1;
  private readonly minCertificateExpirationInMonths = 1;
  private readonly maxCertificateExpirationInMonths = 120;

  addQuestionnaire: boolean = false;

  get isImportQuestionsVisible(): boolean {
    return environment.name === NomEnvironment.Development
      || environment.name === NomEnvironment.Essais;
  }

  // Aperçu du contenu personnalisé désactivé temporairement (à réactiver plus tard)
  // apercuVisible: boolean = false;

  statutEnRevision: boolean = false;

  createdPackageId: number | null = null;
  statutLimiteDemo: StatutLimiteDemoModel | null = null;

  editPackageId: number | null = null;
  isLoadingPackage = false;
  isWaitingForPackageName = false;
  existingPdfByLanguage: Record<LanguageCode, boolean> = { FR: false, EN: false, ES: false };

  lastDraftSavedAt: Date | null = null;

  private pristineState = '';
  private pristineSnapshotKey = '';
  private lastPersistedSnapshotKey = '';
  private draftKey = '';
  private isDraftRestored = false;
  private pendingDraftEntities: { isForWholeGroup: boolean; entityIds: number[] } | null = null;
  private autosaveSubscription?: Subscription;
  private readonly autosaveIntervalInMs = 2000;
  private readonly draftMaxAgeInMs = 7 * 24 * 60 * 60 * 1000;

  get isEditMode(): boolean {
    return this.editPackageId !== null;
  }

  // La modal est maintenant gérée par MatDialog

  constructor(
    private packagesPersoService: PackagesPersoService,
    private utilisateurService: UtilisateurService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private dialog: MatDialog,
    private draftService: PackagePersoDraftService
  ) { }

  ngOnInit(): void {
    this.selectedLanguages.FR = true;
    this.ensureConclusionPair();

    const idParam = this.route.snapshot.paramMap.get('id');
    this.editPackageId = idParam ? +idParam : null;

    this.draftKey = this.buildDraftKey();
    this.capturePristineState();
    this.draftService.purgeExpired(this.draftMaxAgeInMs);

    if (this.editPackageId === null) {
      this.initializeNewPackage();
    }

    this.startAutosave();
    this.startVimeoIdValidation();

    this.utilisateurService.obtenirContexte().subscribe((contexte: ContexteUtilisateur) => {
      this.entitesAccessibles = contexte.entitesAccessibles
        .filter((re: EntiteModel) => !re.estGroupe)
        .map((re: EntiteModel) => ({ id: re.entiteId, nom: re.nom }));

      const groupe = contexte.entitesAccessibles.find((re: EntiteModel) => re.estGroupe);
      if (groupe) {
        this.groupeId = groupe.entiteId;
        this.estPiloteGroupe = true;
        // Par défaut, la formation est attribuée à l'ensemble du groupe.
        this.estPourToutGroupe = true;
      }

      if (this.entitesAccessibles.length === 1) {
        this.entitesSelectionnees = [...this.entitesAccessibles];
        this.entitesDisponibles = [];
      } else {
        this.mettreAJourEntitesDisponibles();
      }

      this.applyPendingDraftEntities();

      if (!this.isDraftRestored) {
        this.capturePristineState();
      }

      if (this.editPackageId !== null) {
        this.loadPackageForEdit(this.editPackageId);
      }
    });
  }

  ngOnDestroy(): void {
    this.videoStatusPolls.forEach(subscription => subscription.unsubscribe());
    this.videoStatusPolls.clear();
    this.autosaveSubscription?.unsubscribe();
    this.vimeoIdValidationSubscription?.unsubscribe();
    this.vimeoIdToValidate.complete();
    this.persistDraftIfChanged();
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.persistDraftIfChanged();
  }

  goBackToList(): void {
    this.router.navigate(['/scorm-perso-managment']);
  }

  hasUnsavedChanges(): boolean {
    if (this.statutEnRevision || this.estEnCoursDeEnregistrement) {
      return false;
    }

    return this.buildDirtyKey() !== this.pristineState;
  }

  private capturePristineState(): void {
    this.pristineState = this.buildDirtyKey();
    this.pristineSnapshotKey = this.buildSnapshotKey();
    this.lastPersistedSnapshotKey = this.pristineSnapshotKey;
  }

  private buildDraftKey(): string {
    const utilisateurId = this.utilisateurService.entiteUtilisateurSelectionnee?.utilisateurId ?? 0;

    return this.draftService.buildKey(utilisateurId, this.editPackageId);
  }

  private startAutosave(): void {
    this.autosaveSubscription = interval(this.autosaveIntervalInMs)
      .subscribe(() => this.persistDraftIfChanged());
  }

  private persistDraftIfChanged(): void {
    if (this.isWaitingForPackageName ||
        this.estEnCoursDeEnregistrement ||
        this.isLoadingPackage) {
      return;
    }

    const snapshotKey = this.buildSnapshotKey();

    if (snapshotKey === this.lastPersistedSnapshotKey) {
      return;
    }

    this.lastPersistedSnapshotKey = snapshotKey;

    if (snapshotKey === this.pristineSnapshotKey) {
      this.discardDraft();

      return;
    }

    const state = this.buildDraftState();
    const files = this.buildDraftFiles();

    this.draftService.save(this.draftKey, state, files)
      .then(savedAt => this.lastDraftSavedAt = savedAt)
      .catch(exc => console.error('Sauvegarde automatique du brouillon impossible:', exc));
  }

  /** Signature complète du formulaire, fichiers inclus (JSON ignore les objets File). */
  private buildSnapshotKey(): string {
    const files = this.buildDraftFiles();
    const fileSignatures = Object.keys(files)
      .sort()
      .map(key => `${key}|${files[key].name}|${files[key].size}|${files[key].lastModified}`);

    return JSON.stringify({ state: this.buildDraftState(), files: fileSignatures });
  }

  private buildDraftState(): PackagePersoDraftState {
    const questions = this.package.questions.map(question => ({
      ...question,
      imageFR: null,
      imageEN: null,
      imageES: null
    }));

    const videos = this.uploadedVideos.map(video => ({
      id: video.id,
      videoFile: null,
      vimeoId: video.vimeoId,
      languages: [...video.languages]
    }));

    const attachedDocuments = this.package.attachedDocuments.map(document => ({
      id: document.id,
      file: null,
      titre: document.titre,
      fileName: document.fileName
    }));

    return {
      packageName: this.nomPackage,
      scormVersion: this.scormVersion,
      selectedLanguages: { ...this.selectedLanguages },
      existingPdfByLanguage: { ...this.existingPdfByLanguage },
      isForWholeGroup: this.estPourToutGroupe,
      entityIds: this.entitesSelectionnees.map(entite => entite.id),
      addQuestionnaire: this.addQuestionnaire,
      package: {
        ...this.package,
        questions,
        videos,
        attachedDocuments,
        pdfFileFR: null,
        pdfFileEN: null,
        pdfFileES: null,
        subtitleFileFR: null,
        subtitleFileEN: null,
        subtitleFileES: null,
        thumbnail: null
      }
    };
  }

  /** Les vidéos sont exclues : elles sont déjà téléversées sur Vimeo, seul le vimeoId compte. */
  private buildDraftFiles(): Record<string, File> {
    const files: Record<string, File> = {};

    if (this.package.pdfFileFR) files['pdfFR'] = this.package.pdfFileFR;
    if (this.package.pdfFileEN) files['pdfEN'] = this.package.pdfFileEN;
    if (this.package.pdfFileES) files['pdfES'] = this.package.pdfFileES;
    if (this.package.subtitleFileFR) files['subtitleFR'] = this.package.subtitleFileFR;
    if (this.package.subtitleFileEN) files['subtitleEN'] = this.package.subtitleFileEN;
    if (this.package.subtitleFileES) files['subtitleES'] = this.package.subtitleFileES;
    if (this.currentThumbnail) files['thumbnail'] = this.currentThumbnail;

    for (const question of this.package.questions) {
      const image = this.getQuestionImage(question);

      if (image) {
        files[`questionImage_${question.id}`] = image;
      }
    }

    for (const document of this.package.attachedDocuments) {
      if (document.file) {
        files[`attachedDocument_${document.id}`] = document.file;
      }
    }

    return files;
  }

  private async initializeNewPackage(): Promise<void> {
    this.isWaitingForPackageName = true;

    const isRestored = await this.tryRestoreDraft();

    if (!isRestored) {
      this.askForPackageName();
    }
  }

  private async tryRestoreDraft(): Promise<boolean> {
    const draft = await this.draftService.get<PackagePersoDraftState>(this.draftKey);

    if (draft === null) {
      return false;
    }

    const dialogData: RestoreDraftDialogData = {
      savedAt: draft.savedAt,
      hasFiles: Object.keys(draft.files).length > 0
    };

    const dialogRef = this.dialog.open(RestoreDraftDialogComponent, {
      width: '520px',
      disableClose: true,
      data: dialogData
    });

    const isRestoreConfirmed: boolean = await firstValueFrom(dialogRef.afterClosed());

    if (!isRestoreConfirmed) {
      await this.draftService.delete(this.draftKey);

      return false;
    }

    this.applyDraft(draft);

    return true;
  }

  private applyDraft(draft: PackagePersoDraft<PackagePersoDraftState>): void {
    const state = draft.state;

    this.nomPackage = state.packageName;
    this.scormVersion = state.scormVersion;
    this.selectedLanguages = { ...state.selectedLanguages };
    this.existingPdfByLanguage = { ...state.existingPdfByLanguage };
    this.addQuestionnaire = state.addQuestionnaire;

    this.package = {
      ...state.package,
      dateFin: state.package.dateFin ? new Date(state.package.dateFin) : null,
      pdfFileFR: draft.files['pdfFR'] ?? null,
      pdfFileEN: draft.files['pdfEN'] ?? null,
      pdfFileES: draft.files['pdfES'] ?? null,
      subtitleFileFR: draft.files['subtitleFR'] ?? null,
      subtitleFileEN: draft.files['subtitleEN'] ?? null,
      subtitleFileES: draft.files['subtitleES'] ?? null,
      thumbnail: draft.files['thumbnail'] ?? null
    };

    this.currentThumbnail = this.package.thumbnail;

    for (const question of this.package.questions) {
      const image = draft.files[`questionImage_${question.id}`] ?? null;
      this.setQuestionImage(question, image);
      this.previousQuestionTypeById.set(question.id, question.type);
    }

    for (const document of this.package.attachedDocuments) {
      document.file = draft.files[`attachedDocument_${document.id}`] ?? null;
    }

    this.uploadedVideos = (state.package.videos ?? []).map(video => ({
      id: video.id,
      videoFile: null,
      vimeoId: video.vimeoId,
      languages: [...video.languages]
    }));
    this.package.videos = [...this.uploadedVideos];
    this.uploadedVideos.forEach(video => this.pollVideoStatus(video.vimeoId));

    this.ensureConclusionPair();

    this.pendingDraftEntities = { isForWholeGroup: state.isForWholeGroup, entityIds: state.entityIds };
    this.applyPendingDraftEntities();

    this.isDraftRestored = true;
    this.isWaitingForPackageName = false;
    this.lastDraftSavedAt = draft.savedAt;
    this.lastPersistedSnapshotKey = this.buildSnapshotKey();

    this.snackBar.open(
      this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DRAFT_RESTORED'),
      'Fermer',
      { duration: 5000 });
  }

  private applyPendingDraftEntities(): void {
    if (this.pendingDraftEntities === null || this.entitesAccessibles.length === 0) {
      return;
    }

    const draftEntities = this.pendingDraftEntities;

    this.estPourToutGroupe = draftEntities.isForWholeGroup;
    this.entitesSelectionnees = this.entitesAccessibles
      .filter(entite => draftEntities.entityIds.includes(entite.id));
    this.mettreAJourEntitesDisponibles();

    this.pendingDraftEntities = null;
  }

  /** Le brouillon local n'a plus de raison d'exister : l'état est enregistré côté serveur. */
  private discardDraft(): void {
    const previousKey = this.draftKey;

    this.draftKey = this.buildDraftKey();
    this.isDraftRestored = false;
    this.lastDraftSavedAt = null;

    this.draftService.delete(previousKey)
      .catch(exc => console.error('Suppression du brouillon local impossible:', exc));

    if (this.draftKey !== previousKey) {
      this.draftService.delete(this.draftKey)
        .catch(exc => console.error('Suppression du brouillon local impossible:', exc));
    }
  }

  private askForPackageName(): void {
    this.isWaitingForPackageName = true;

    const dialogRef = this.dialog.open(PackageNameDialogComponent, {
      width: '520px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((packageName: string | null) => {
      if (!packageName) {
        this.goBackToList();

        return;
      }

      this.applyPackageName(packageName);
      this.isWaitingForPackageName = false;
    });
  }

  private applyPackageName(packageName: string): void {
    this.nomPackage = packageName;
    this.package.nomPackage = packageName;
    this.package.nomFR = packageName;

    this.capturePristineState();
  }

  private buildDirtyKey(): string {
    const snapshot = {
      package: this.package,
      nomPackage: this.nomPackage,
      selectedLanguages: this.selectedLanguages,
      failConclusion: this.failConclusion,
      passConclusion: this.passConclusion,
      estPourToutGroupe: this.estPourToutGroupe,
      entiteIds: this.entitesSelectionnees.map(entite => entite.id)
    };

    return JSON.stringify(snapshot);
  }

  private loadPackageForEdit(packageId: number): void {
    this.isLoadingPackage = true;

    this.packagesPersoService.getPackageForEdit(packageId).subscribe({
      next: (model: PackagePersoEditModel) => {
        this.isLoadingPackage = false;
        if (!model.isDraft) {
          this.router.navigate(['/scorm-perso-managment/edit', packageId]);
          return;
        }
        this.rehydrate(model);
        this.tryRestoreDraft();
      },
      error: err => {
        this.isLoadingPackage = false;
        this.snackBar.open(this.translate.instant('PACKAGES_PERSO_MANAGEMENT.LOAD_DRAFT_ERROR'), 'Fermer', { duration: 5000 });
        console.error('Erreur:', err);
        this.goBack();
      }
    });
  }

  private rehydrate(model: PackagePersoEditModel): void {
    this.nomPackage = model.packageName;
    this.scormVersion = model.scormVersion;
    this.package.nomPackage = model.packageName;
    this.package.scormVersion = model.scormVersion;

    this.selectedLanguages = {
      FR: model.hasFR,
      EN: model.hasEN,
      ES: model.hasES
    };

    this.package.nomFR = model.nameFR ?? '';
    this.package.descriptionFR = model.descriptionFR ?? '';
    this.package.nomEN = model.nameEN ?? '';
    this.package.descriptionEN = model.descriptionEN ?? '';
    this.package.nomES = model.nameES ?? '';
    this.package.descriptionES = model.descriptionES ?? '';

    this.package.contentType = model.contentType;
    this.package.dureeEnMinutes = model.durationInMinutes ?? undefined;
    this.package.pourcentageSucces = model.successPercentage ?? undefined;
    this.package.donneCertificat = model.givesCertificate;
    this.package.expirationCertificatEnMois = model.certificateExpirationInMonths ?? undefined;
    this.package.logoCertificat = model.certificateLogo ?? undefined;
    this.package.dateFin = model.endDate ? new Date(model.endDate) : null;
    this.package.canSeekForward = model.canSeekForward;
    this.package.readingConfirmationRequired = model.readingConfirmationRequired;
    this.package.existingThumbnailFileName = model.thumbnailFileName;

    this.existingPdfByLanguage = {
      FR: model.contentType === 'pdf' && model.pdfLanguages.includes('FR'),
      EN: model.contentType === 'pdf' && model.pdfLanguages.includes('EN'),
      ES: model.contentType === 'pdf' && model.pdfLanguages.includes('ES')
    };

    this.uploadedVideos = (model.videos ?? []).map(video => ({
      id: video.id,
      videoFile: null,
      vimeoId: video.vimeoId ?? undefined,
      languages: video.languages
    }));
    this.package.videos = [...this.uploadedVideos];

    this.uploadedVideos.forEach(video => this.pollVideoStatus(video.vimeoId));

    this.package.questions = (model.questions ?? []).map(question => ({
      id: question.id,
      textFR: question.textFR ?? '',
      textEN: question.textEN ?? '',
      textES: question.textES ?? '',
      type: question.type as QuestionTypeEnum,
      answersAreRandom: question.answersAreRandom ?? false,
      answers: (question.answers ?? []).map(answer => ({
        id: answer.id,
        textFR: answer.textFR ?? '',
        textEN: answer.textEN ?? '',
        textES: answer.textES ?? '',
        isCorrect: answer.isCorrect
      })),
      imageFR: null,
      imageEN: null,
      imageES: null,
      imageFileNameFR: question.imageFileNameFR,
      imageFileNameEN: question.imageFileNameEN,
      imageFileNameES: question.imageFileNameES
    }));
    this.package.questions.forEach(question => this.previousQuestionTypeById.set(question.id, question.type));
    this.addQuestionnaire = this.package.questions.length > 0;

    if (model.quizInfoGenerale && this.package.quizInfoGenerale) {
      const info = this.package.quizInfoGenerale;
      info.posterFrameFR = model.quizInfoGenerale.posterFrameFR ?? '';
      info.posterFrameEN = model.quizInfoGenerale.posterFrameEN ?? '';
      info.posterFrameES = model.quizInfoGenerale.posterFrameES ?? '';
      info.picturesRelativePath = model.quizInfoGenerale.picturesRelativePath ?? '';
      info.isAnswersAreRandom = model.quizInfoGenerale.isAnswersAreRandom;
      info.isAutoValidateSingleSelection = model.quizInfoGenerale.isAutoValidateSingleSelection;
    }

    if (model.quizIntroduction && this.package.quizIntroduction) {
      const intro = this.package.quizIntroduction;
      intro.titreFR = model.quizIntroduction.titreFR ?? '';
      intro.titreEN = model.quizIntroduction.titreEN ?? '';
      intro.titreES = model.quizIntroduction.titreES ?? '';
      intro.sousTitreFR = model.quizIntroduction.sousTitreFR ?? '';
      intro.sousTitreEN = model.quizIntroduction.sousTitreEN ?? '';
      intro.sousTitreES = model.quizIntroduction.sousTitreES ?? '';
    }

    if (model.quizConclusions?.length) {
      this.package.quizConclusions = model.quizConclusions.map(conclusion => ({
        id: conclusion.id,
        scoreMax: conclusion.scoreMax,
        isPassing: conclusion.isPassing,
        titreFR: conclusion.titreFR ?? '',
        titreEN: conclusion.titreEN ?? '',
        titreES: conclusion.titreES ?? '',
        sousTitreFR: conclusion.sousTitreFR ?? '',
        sousTitreEN: conclusion.sousTitreEN ?? '',
        sousTitreES: conclusion.sousTitreES ?? ''
      }));

      if (!this.package.quizConclusions.some(conclusion => conclusion.isPassing === true)) {
        const maxScore = Math.max(...this.package.quizConclusions.map(conclusion => conclusion.scoreMax ?? 0));
        this.package.quizConclusions.forEach(conclusion => {
          conclusion.isPassing = maxScore > 0 && (conclusion.scoreMax ?? 0) === maxScore;
        });
      }
    }
    this.ensureConclusionPair();

    this.package.attachedDocuments = (model.attachedDocuments ?? []).map(document => ({
      id: document.id,
      file: null,
      titre: document.titre ?? '',
      fileName: document.fileName ?? undefined
    }));

    if (model.groupId != null && this.estPiloteGroupe) {
      this.estPourToutGroupe = true;
      this.entitesSelectionnees = [];
    } else if (model.entityIds?.length) {
      this.estPourToutGroupe = false;
      this.entitesSelectionnees = this.entitesAccessibles.filter(entite => model.entityIds.includes(entite.id));
    }
    this.mettreAJourEntitesDisponibles();

    this.createdPackageId = model.scormPackageId;

    this.chargerStatutLimiteDemo();

    this.capturePristineState();
  }

  /** Ajoute une question de type « Choix unique » pré-remplie avec 4 réponses vides (nouvelle UI). */
  addQuestionWithDefaultAnswers(): void {
    const now = Date.now();
    const newQuestion: QuestionModel = {
      id: now,
      textFR: '',
      textEN: '',
      textES: '',
      type: QuestionTypeEnum.UniqueChoice,
      answersAreRandom: false,
      answers: [1, 2, 3, 4].map(offset => ({
        id: now + offset,
        textFR: '',
        textEN: '',
        textES: '',
        isCorrect: false
      })),
      imageFR: null,
      imageEN: null,
      imageES: null
    };
    this.package.questions.push(newQuestion);
  }

  onGlobalAnswersAreRandomChange(isRandom: boolean): void {
    for (const question of this.package.questions) {
      question.answersAreRandom = isRandom;
    }
  }

  get primaryLanguageCode(): LanguageCode {
    return this.getSelectedLanguagesArray()[0] ?? 'FR';
  }

  protected readonly languageLabels: Record<LanguageCode, string> = {
    FR: 'Français',
    EN: 'Anglais',
    ES: 'Espagnol'
  };

  private contentEditingLanguage: LanguageCode | null = null;

  getContentEditingLanguage(): LanguageCode {
    return this.contentEditingLanguage && this.selectedLanguages[this.contentEditingLanguage]
      ? this.contentEditingLanguage
      : this.primaryLanguageCode;
  }

  setContentEditingLanguage(language: LanguageCode): void {
    this.contentEditingLanguage = language;
  }

  isContentLanguageComplete(language: LanguageCode): boolean {
    const name = language === 'EN' ? this.package.nomEN
      : language === 'ES' ? this.package.nomES
      : this.package.nomFR;

    if (!name?.trim()) {
      return false;
    }

    if (this.package.contentType === 'pdf') {
      return !!this.getPdfFileForLanguage(language) || this.existingPdfByLanguage[language];
    }

    return this.hasVideoForLanguage(language);
  }

  private quizEditingLanguage: LanguageCode | null = null;

  getQuizEditingLanguage(): LanguageCode {
    return this.quizEditingLanguage && this.selectedLanguages[this.quizEditingLanguage]
      ? this.quizEditingLanguage
      : this.primaryLanguageCode;
  }

  setQuizEditingLanguage(language: LanguageCode): void {
    this.quizEditingLanguage = language;
  }

  private readonly quizPlaceholderExamples: Record<QuizPlaceholderField, Record<LanguageCode, string>> = {
    introTitre: {
      FR: 'Mettons à l\'épreuve vos connaissances.',
      EN: 'Let\'s put your knowledge to the test.',
      ES: 'Pongamos a prueba sus conocimientos.'
    },
    introSousTitre: {
      FR: 'Vous devez avoir au moins 4 bonnes réponses pour réussir. Bonne chance!',
      EN: 'You need at least 4 correct answers to pass. Good luck!',
      ES: 'Debe obtener al menos 4 respuestas correctas para aprobar. ¡Buena suerte!'
    },
    echecTitre: {
      FR: 'Désolé.',
      EN: 'Sorry.',
      ES: 'Lo sentimos.'
    },
    echecSousTitre: {
      FR: 'Ce n\'est pas suffisant pour maîtriser la matière.',
      EN: 'This is not enough to master the subject.',
      ES: 'No es suficiente para dominar la materia.'
    },
    reussiteTitre: {
      FR: 'Bravo!',
      EN: 'Well done!',
      ES: '¡Bravo!'
    },
    reussiteSousTitre: {
      FR: 'Beau travail! Vous êtes maintenant un expert en la matière. Revenez faire la formation de temps à autre pour vous rafraîchir la mémoire.',
      EN: 'Great job! You are now an expert on the subject. Come back to take the training from time to time to refresh your memory.',
      ES: '¡Buen trabajo! Ahora es un experto en la materia. Vuelva a realizar la formación de vez en cuando para refrescar la memoria.'
    }
  };

  getQuizPlaceholder(field: QuizPlaceholderField, language: LanguageCode): string {
    return this.quizPlaceholderExamples[field][language];
  }

  getQuestionPlaceholder(question: QuestionModel): string {
    return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.ENTER_QUESTION_PLACEHOLDER', {
      language: this.translate.instant('PACKAGES_PERSO_MANAGEMENT.IN_LANGUAGE_' + this.getQuestionEditingLanguage(question))
    });
  }

  private questionEditingLanguageById = new Map<number, LanguageCode>();

  getQuestionEditingLanguage(question: QuestionModel): LanguageCode {
    const language = this.questionEditingLanguageById.get(question.id);
    return language && this.selectedLanguages[language] ? language : this.primaryLanguageCode;
  }

  setQuestionEditingLanguage(question: QuestionModel, language: LanguageCode): void {
    this.questionEditingLanguageById.set(question.id, language);
  }

  getQuestionText(question: QuestionModel): string {
    switch (this.getQuestionEditingLanguage(question)) {
      case 'EN': return question.textEN;
      case 'ES': return question.textES;
      default: return question.textFR;
    }
  }

  setQuestionText(question: QuestionModel, value: string): void {
    switch (this.getQuestionEditingLanguage(question)) {
      case 'EN': question.textEN = value; break;
      case 'ES': question.textES = value; break;
      default: question.textFR = value; break;
    }
  }

  getAnswerText(question: QuestionModel, answer: AnswerModel): string {
    switch (this.getQuestionEditingLanguage(question)) {
      case 'EN': return answer.textEN;
      case 'ES': return answer.textES;
      default: return answer.textFR;
    }
  }

  setAnswerText(question: QuestionModel, answer: AnswerModel, value: string): void {
    switch (this.getQuestionEditingLanguage(question)) {
      case 'EN': answer.textEN = value; break;
      case 'ES': answer.textES = value; break;
      default: answer.textFR = value; break;
    }
  }

  hasAnswerText(answer: AnswerModel): boolean {
    return !!answer.textFR?.trim() || !!answer.textEN?.trim() || !!answer.textES?.trim();
  }

  isQuestionLanguageComplete(question: QuestionModel, language: LanguageCode): boolean {
    const textOf = (texts: { textFR: string; textEN: string; textES: string }): string => {
      switch (language) {
        case 'EN': return texts.textEN;
        case 'ES': return texts.textES;
        default: return texts.textFR;
      }
    };
    return !!textOf(question)?.trim() && question.answers.every(answer => !!textOf(answer)?.trim());
  }

  private previousQuestionTypeById = new Map<number, QuestionTypeEnum>();

  onQuestionTypeChange(question: QuestionModel): void {
    const previousType = this.previousQuestionTypeById.get(question.id);
    this.previousQuestionTypeById.set(question.id, question.type);

    if (question.type === QuestionTypeEnum.TrueFalse) {
      question.answers = [
        { id: Date.now(), textFR: 'Vrai', textEN: 'True', textES: 'Verdadero', isCorrect: true },
        { id: Date.now() + 1, textFR: 'Faux', textEN: 'False', textES: 'Falso', isCorrect: false },
      ];
      return;
    }

    // En quittant « Vrai/Faux » pour un type à choix, on restaure des options vides (Option 1, 2, …)
    if (previousType === QuestionTypeEnum.TrueFalse &&
        (question.type === QuestionTypeEnum.UniqueChoice || question.type === QuestionTypeEnum.MultipleChoice)) {
      const now = Date.now();
      question.answers = [1, 2, 3, 4].map(offset => ({
        id: now + offset,
        textFR: '',
        textEN: '',
        textES: '',
        isCorrect: false
      }));
      return;
    }

    if (question.type === QuestionTypeEnum.FillIn) {
      const existing = question.answers[0];
      question.answers = [{
        id: existing?.id ?? Date.now(),
        textFR: existing?.textFR ?? '',
        textEN: existing?.textEN ?? '',
        textES: existing?.textES ?? '',
        isCorrect: true,
      }];
      return;
    }

    if (question.type === QuestionTypeEnum.UniqueChoice) {
      const correct = question.answers.filter(a => a.isCorrect);
      if (correct.length > 1) {
        correct.slice(1).forEach(a => a.isCorrect = false);
      }
    }
  }

  addAnswer(question: QuestionModel): void {
    if (question.answers.length >= this.maxAnswersPerQuestion) {
      this.snackBar.open(
        this.translate.instant('PACKAGES_PERSO_MANAGEMENT.MAXIMUM_ANSWERS_REACHED', { max: this.maxAnswersPerQuestion }),
        'Fermer',
        { duration: 5000 });

      return;
    }

    const newAnswer: AnswerModel = {
      id: Date.now(),
      textFR: '',
      textEN: '',
      textES: '',
      isCorrect: false
    };
    question.answers.push(newAnswer);
  }

  removeQuestion(questionId: number): void {
    this.package.questions = this.package.questions.filter(q => q.id !== questionId);
    this.questionEditingLanguageById.delete(questionId);
  }

  removeAnswer(question: QuestionModel, answerId: number): void {
    question.answers = question.answers.filter(a => a.id !== answerId);
  }

  onImportQuestionsClick(fileInput: HTMLInputElement): void {
    fileInput.value = '';
    fileInput.click();
  }

  onImportQuestionsFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = JSON.parse(reader.result as string) as RawQuestionnaireJson;
        const response = this.mapRawQuestionnaireToResponse(raw);
        this.applyQuizInfosFromApi(response);
      } catch (err) {
        console.error('Erreur lors de l\'import des questions:', err);
        this.snackBar.open(this.translate.instant('PACKAGES_PERSO_MANAGEMENT.IMPORTER_QUESTIONS_ERREUR'), 'Fermer', { duration: 5000 });
      }
    };
    reader.onerror = (err) => {
      console.error('Erreur lors de la lecture du fichier:', err);
      this.snackBar.open(this.translate.instant('PACKAGES_PERSO_MANAGEMENT.IMPORTER_QUESTIONS_ERREUR'), 'Fermer', { duration: 5000 });
    };
    reader.readAsText(file);
  }

  private mapRawQuestionnaireToResponse(raw: RawQuestionnaireJson): GetQuizInfosFromJsonResponse {
    const rawLangs: ('fr' | 'en' | 'es')[] = ['fr', 'en', 'es'];
    const isRawLang = (l: string): l is 'fr' | 'en' | 'es' => l === 'fr' || l === 'en' || l === 'es';

    const titleHtmlByLang = raw.title?.html ?? {};
    const quizTitleDtos = rawLangs
      .filter(lang => titleHtmlByLang[lang] != null)
      .map(lang => ({ language: lang, titleHtml: titleHtmlByLang[lang] ?? null }));

    const posterByLang = raw.posterFrame ?? {};
    const quizPosterFrameDtos = rawLangs
      .filter(lang => posterByLang[lang] != null)
      .map(lang => ({ language: lang, fileName: posterByLang[lang] ?? null }));

    const introTitleByLang = raw.introMessage?.title?.html ?? {};
    const introSubtitleByLang = raw.introMessage?.subtitle?.html ?? {};
    const introLangs = Array.from(new Set([
      ...Object.keys(introTitleByLang),
      ...Object.keys(introSubtitleByLang),
    ])).filter(isRawLang);
    const quizIntroMessageDtos = introLangs.map(lang => ({
      language: lang,
      titleHtml: introTitleByLang[lang] ?? null,
      subtitleHtml: introSubtitleByLang[lang] ?? null,
    }));

    const quizOutroMessageDtos = (raw.outroMessages ?? []).map((o, idx) => {
      const oTitle = o.title?.html ?? {};
      const oSubtitle = o.subtitle?.html ?? {};
      const oContinue = o.continueButton?.html ?? {};
      const langs = Array.from(new Set([
        ...Object.keys(oTitle),
        ...Object.keys(oSubtitle),
        ...Object.keys(oContinue),
      ])).filter(isRawLang);
      const quizOutroMessageTextDtos: QuizOutroMessageTextDto[] = langs.map(lang => ({
        language: lang,
        titleHtml: oTitle[lang] ?? '',
        subtitleHtml: oSubtitle[lang] ?? '',
        continueButtonHtml: oContinue[lang] ?? '',
      }));
      return {
        scoreMax: typeof o.scoreMax === 'number' ? o.scoreMax : 0,
        sortOrder: idx,
        quizOutroMessageTextDtos,
      };
    });

    const questionEntries = Object.entries(raw.questions ?? {})
      .sort(([a], [b]) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));

    const quizQuestionDtos: QuizQuestionDto[] = questionEntries.map(([qid, q], idx) => {
      const qHtml = q.html ?? {};
      const quizQuestionTextDtos = rawLangs
        .filter(lang => qHtml[lang] != null)
        .map(lang => ({ language: lang, questionHtml: qHtml[lang] ?? '' }));

      const answerEntries = Object.entries(q.answers ?? {})
        .sort(([a], [b]) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));

      const quizQuestionAnswerDtos: QuizQuestionAnswerDto[] = answerEntries.map(([aid, a], aidx) => {
        const aHtml = a.html ?? {};
        const quizQuestionAnswerTextDtos = rawLangs
          .filter(lang => aHtml[lang] != null)
          .map(lang => ({ language: lang, answerHtml: aHtml[lang] ?? '' }));
        return {
          isCorrect: !!a.right,
          sortOrder: parseInt(aid, 10) || aidx,
          quizQuestionAnswerTextDtos,
        };
      });

      const correctCount = quizQuestionAnswerDtos.filter(a => a.isCorrect).length;
      return {
        questionIdentifier: qid,
        isAnswersAreRandom: q.answersAreRandom ?? !!raw.answersAreRandom,
        isMultiSelection: correctCount > 1,
        sortOrder: parseInt(qid, 10) || idx,
        quizQuestionTextDtos,
        quizQuestionAnswerDtos,
      };
    });

    return {
      quizPath: '',
      quizDto: {
        quizId: 0,
        scormPackageId: 0,
        picturesRelativePath: raw.imagesRelativePath ?? null,
        isAnswersAreRandom: !!raw.answersAreRandom,
        isAutoValidateSingleSelection: !!raw.autoValidateSingleSelection,
      },
      quizTitleDtos,
      quizPosterFrameDtos,
      quizIntroMessageDtos,
      quizOutroMessageDtos,
      quizQuestionDtos,
    };
  }

  private applyQuizInfosFromApi(response: GetQuizInfosFromJsonResponse): void {
    const importedQuestions = this.mapApiQuestionsToModel(response.quizQuestionDtos ?? []);

    if (importedQuestions.length === 0 && (response.quizTitleDtos?.length ?? 0) === 0) {
      this.snackBar.open(this.translate.instant('PACKAGES_PERSO_MANAGEMENT.IMPORTER_QUESTIONS_ERREUR'), 'Fermer', { duration: 5000 });
      return;
    }

    this.package.questions = [...this.package.questions, ...importedQuestions];
    this.applyApiQuizInfo(response);
    this.applyApiIntroduction(response);
    this.applyApiOutros(response);

    this.snackBar.open(
      this.translate.instant('PACKAGES_PERSO_MANAGEMENT.IMPORTER_QUESTIONS_SUCCES', { count: importedQuestions.length }),
      'Fermer',
      { duration: 5000 }
    );
  }

  private applyApiQuizInfo(response: GetQuizInfosFromJsonResponse): void {
    if (!this.package.quizInfoGenerale) return;
    const info = this.package.quizInfoGenerale;

    const posterByLang = this.indexByLanguage(response.quizPosterFrameDtos ?? [], p => p.fileName ?? '');
    info.posterFrameFR = posterByLang.FR ?? info.posterFrameFR;
    info.posterFrameEN = posterByLang.EN ?? info.posterFrameEN;
    info.posterFrameES = posterByLang.ES ?? info.posterFrameES;

    if (response.quizDto) {
      info.picturesRelativePath = response.quizDto.picturesRelativePath ?? info.picturesRelativePath;
      info.isAnswersAreRandom = !!response.quizDto.isAnswersAreRandom;
      info.isAutoValidateSingleSelection = !!response.quizDto.isAutoValidateSingleSelection;
    }
  }

  private applyApiIntroduction(response: GetQuizInfosFromJsonResponse): void {
    if (!this.package.quizIntroduction) return;
    const intro = this.package.quizIntroduction;
    const intros = response.quizIntroMessageDtos ?? [];

    const titleByLang = this.indexByLanguage(intros, m => m.titleHtml ?? '');
    const subtitleByLang = this.indexByLanguage(intros, m => m.subtitleHtml ?? '');

    intro.titreFR = titleByLang.FR ?? intro.titreFR;
    intro.titreEN = titleByLang.EN ?? intro.titreEN;
    intro.titreES = titleByLang.ES ?? intro.titreES;
    intro.sousTitreFR = subtitleByLang.FR ?? intro.sousTitreFR;
    intro.sousTitreEN = subtitleByLang.EN ?? intro.sousTitreEN;
    intro.sousTitreES = subtitleByLang.ES ?? intro.sousTitreES;
  }

  private applyApiOutros(response: GetQuizInfosFromJsonResponse): void {
    const outros = response.quizOutroMessageDtos ?? [];
    if (outros.length === 0) return;

    const scoreMaxValues = outros
      .map(o => o.scoreMax)
      .filter((s): s is number => typeof s === 'number');
    if (scoreMaxValues.length > 0) {
      this.package.pourcentageSucces = Math.ceil(Math.min(...scoreMaxValues));
    }

    let nextId = Date.now();
    const threshold = this.package.pourcentageSucces;
    const conclusions: QuizConclusionModel[] = outros
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((o) => {
        const texts = o.quizOutroMessageTextDtos ?? [];
        const titleByLang = this.indexByLanguage<QuizOutroMessageTextDto>(texts, t => t.titleHtml ?? '');
        const subtitleByLang = this.indexByLanguage<QuizOutroMessageTextDto>(texts, t => t.subtitleHtml ?? '');
        return {
          id: nextId++,
          scoreMax: typeof o.scoreMax === 'number' ? o.scoreMax : null,
          isPassing: typeof o.scoreMax === 'number' && threshold !== null && threshold !== undefined
            ? o.scoreMax > threshold
            : false,
          titreFR: titleByLang.FR ?? '',
          titreEN: titleByLang.EN ?? '',
          titreES: titleByLang.ES ?? '',
          sousTitreFR: subtitleByLang.FR ?? '',
          sousTitreEN: subtitleByLang.EN ?? '',
          sousTitreES: subtitleByLang.ES ?? '',
        };
      });

    this.package.quizConclusions = conclusions;
  }

  private mapApiQuestionsToModel(dtos: QuizQuestionDto[]): QuestionModel[] {
    let nextId = Date.now();
    return dtos
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((q) => {
        const questionTexts = this.indexByLanguage(q.quizQuestionTextDtos ?? [], t => t.questionHtml ?? '');

        const answers: AnswerModel[] = (q.quizQuestionAnswerDtos ?? [])
          .slice()
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((a: QuizQuestionAnswerDto) => {
            const answerTexts = this.indexByLanguage(a.quizQuestionAnswerTextDtos ?? [], t => t.answerHtml ?? '');
            return {
              id: nextId++,
              textFR: answerTexts.FR ?? '',
              textEN: answerTexts.EN ?? '',
              textES: answerTexts.ES ?? '',
              isCorrect: !!a.isCorrect,
            };
          });

        const correctCount = answers.filter(a => a.isCorrect).length;
        const type: QuestionTypeEnum = q.isMultiSelection || correctCount > 1
          ? QuestionTypeEnum.MultipleChoice
          : QuestionTypeEnum.UniqueChoice;

        return {
          id: nextId++,
          textFR: questionTexts.FR ?? '',
          textEN: questionTexts.EN ?? '',
          textES: questionTexts.ES ?? '',
          type,
          answersAreRandom: !!q.isAnswersAreRandom,
          answers,
          imageFR: null,
          imageEN: null,
          imageES: null,
        };
      });
  }

  private indexByLanguage<T extends { language: LanguageDto }>(items: T[], extract: (item: T) => string): { FR?: string; EN?: string; ES?: string } {
    const result: { FR?: string; EN?: string; ES?: string } = {};
    for (const item of items) {
      const code = this.toLanguageCode(item.language);
      if (code) {
        result[code] = extract(item);
      }
    }
    return result;
  }

  private toLanguageCode(language: LanguageDto): LanguageCode | null {
    if (typeof language === 'string') {
      const normalized = language.toLowerCase();
      if (normalized === 'fr') return 'FR';
      if (normalized === 'en') return 'EN';
      if (normalized === 'es') return 'ES';
      return null;
    }
    if (language === 0) return 'FR';
    if (language === 1) return 'EN';
    if (language === 2) return 'ES';
    return null;
  }

  setAnswerCorrect(question: QuestionModel, answer: AnswerModel): void {
    if (question.type === QuestionTypeEnum.UniqueChoice ||
        question.type === QuestionTypeEnum.TrueFalse) {
      question.answers.forEach(a => a.isCorrect = false);
    }
    answer.isCorrect = true;
  }

  onVideoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.demarrerUploadTus(input.files[0]);
    }
  }

  private demarrerUploadTus(file: File): void {
    this.currentVideoUpload = file;
    this.isVideoUploading = true;
    this.videoUploadProgress = 0;
    this.videoFileError = '';

    const utilisateurId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;

    this.packagesPersoService.initierUploadVideo(file.name, file.size, utilisateurId).subscribe({
      next: (lienUpload) => {
        const upload = new tus.Upload(file, {
          uploadUrl: lienUpload.lienUpload,
          chunkSize: 50 * 1024 * 1024,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          onProgress: (bytesUploaded: number, bytesTotal: number) => {
            this.videoUploadProgress = Math.round(bytesUploaded / bytesTotal * 100);
          },
          onSuccess: () => {
            this.currentVimeoId = lienUpload.vimeoId;
            this.currentVideoUpload = null;
            this.isVideoUploading = false;
            this.videoUploadProgress = 0;
            this.pollVideoStatus(lienUpload.vimeoId);
          },
          onError: (error: Error) => {
            this.isVideoUploading = false;
            this.videoUploadProgress = 0;
            this.currentVideoUpload = null;
            this.videoFileError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.VIDEO_UPLOAD_ERROR');
            console.error('TUS upload error:', error);
          }
        });
        upload.start();
      },
      error: (err) => {
        this.isVideoUploading = false;
        this.videoUploadProgress = 0;
        this.currentVideoUpload = null;
        this.videoFileError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.VIDEO_UPLOAD_ERROR');
        console.error('Error initiating upload:', err);
      }
    });
  }

  resetCurrentVideo(): void {
    this.currentVideoUpload = null;
    this.currentVimeoId = null;
    this.vimeoIdError = '';
    this.isVideoUploading = false;
    this.videoUploadProgress = 0;
  }

  hasSelectedLanguagesForVideo(): boolean {
    return Object.values(this.selectedLanguagesForVideo).some(value => value);
  }

  addVideo(): void {
    if (!this.currentVideoUpload && !this.currentVimeoId) return;

    const selectedLangs = Object.entries(this.selectedLanguagesForVideo)
      .filter(([_, selected]) => selected)
      .map(([lang]) => lang as LanguageCode);

    if (selectedLangs.length === 0) return;

    this.uploadedVideos = this.uploadedVideos.filter(video => {
      return !video.languages.some(lang => selectedLangs.includes(lang));
    });

    const newVideo: VideoAssociation = {
      id: Date.now(),
      videoFile: this.currentVideoUpload,
      vimeoId: this.currentVimeoId || undefined,
      languages: selectedLangs
    };

    this.uploadedVideos.push(newVideo);
    this.package.videos = [...this.uploadedVideos];

    this.currentVideoUpload = null;
    this.currentVimeoId = null;
    this.resetVideoLanguageSelection();
  }

  resetVideoLanguageSelection(): void {
    Object.keys(this.selectedLanguagesForVideo).forEach(lang => {
      this.selectedLanguagesForVideo[lang] = false;
    });
    this.vimeoIdError = '';
  }

  removeVideo(videoId: number): void {
    this.uploadedVideos = this.uploadedVideos.filter(v => v.id !== videoId);
    this.package.videos = [...this.uploadedVideos];
  }

  editVideoLanguages(videoId: number): void {
    const video = this.uploadedVideos.find(v => v.id === videoId);
    if (!video) return;

    const dialogData: EditVideoLanguageModalData = {
      video,
      selectedLanguages: this.selectedLanguages,
      allVideos: this.uploadedVideos
    };

    const dialogRef = this.dialog.open(EditVideoLanguageModalComponent, {
      width: '500px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((selectedLanguages: LanguageCode[]) => {
      if (selectedLanguages !== undefined) {
        this.saveVideoLanguageChanges(video, selectedLanguages);
      }
    });
  }

  private saveVideoLanguageChanges(video: VideoAssociation, selectedLanguages: LanguageCode[]): void {
    if (selectedLanguages.length === 0) {
      console.warn('Aucune langue sélectionnée - opération annulée');
      return;
    }

    const videoIndex = this.uploadedVideos.findIndex(v => v.id === video.id);
    if (videoIndex !== -1) {
      this.uploadedVideos[videoIndex] = {
        ...this.uploadedVideos[videoIndex],
        languages: selectedLanguages
      };
      this.package.videos = [...this.uploadedVideos];
    }
  }

  stripNonDigits(input: HTMLInputElement): void {
    input.value = input.value.replace(/\D/g, '');
  }

  onVimeoIdInput(vimeoId: string): void {
    if (vimeoId && !this.validateVimeoId(vimeoId)) {
      this.vimeoIdError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.INVALID_VIMEO_ID');
      this.currentVimeoId = null;
    } else {
      this.vimeoIdError = '';
      this.currentVimeoId = vimeoId;
      this.pollVideoStatus(vimeoId);
    }
  }

  onSubtitleChange(subtitle: File | null, language: LanguageCode): void {
    switch (language) {
      case 'FR':
        this.package.subtitleFileFR = subtitle;
        break;
      case 'EN':
        this.package.subtitleFileEN = subtitle;
        break;
      case 'ES':
        this.package.subtitleFileES = subtitle;
        break;
    }
  }

  hasVideoForLanguage(language: LanguageCode): boolean {
    return this.uploadedVideos.some(video =>
      video.languages.includes(language) && (video.videoFile || video.vimeoId)
    );
  }

  allVideosReady(): boolean {
    return this.uploadedVideos
      .filter(video => !!video.vimeoId)
      .every(video => this.videoStatuses.get(video.vimeoId!) === 'ready');
  }

  hasProcessingVideos(): boolean {
    return this.uploadedVideos.some(video =>
      !!video.vimeoId && (this.videoStatuses.get(video.vimeoId) ?? 'processing') === 'processing'
    );
  }

  hasVideoError(): boolean {
    return this.uploadedVideos.some(video =>
      !!video.vimeoId && this.videoStatuses.get(video.vimeoId) === 'error'
    );
  }

  getVimeoIdForLanguage(language: LanguageCode): string {
    const video = this.uploadedVideos.find(v => v.languages.includes(language));
    return video?.vimeoId ?? '';
  }

  getVideoFileForLanguage(language: LanguageCode): File | null {
    const video = this.uploadedVideos.find(v => v.languages.includes(language));
    return video?.videoFile ?? null;
  }

  onLanguageVimeoIdChange(language: LanguageCode, vimeoId: string): void {
    const trimmed = vimeoId?.trim() ?? '';
    this.removeLanguageFromVideos(language);
    if (trimmed) {
      this.uploadedVideos.push({
        id: Date.now(),
        videoFile: null,
        vimeoId: trimmed,
        languages: [language]
      });
      this.pollVideoStatus(trimmed);
      this.vimeoIdToValidate.next(trimmed);
    }
    this.package.videos = [...this.uploadedVideos];
  }

  getVimeoIdErrorForLanguage(language: LanguageCode): string {
    const vimeoId = this.getVimeoIdForLanguage(language);

    if (!vimeoId) {
      return '';
    }

    return this.vimeoIdErrorsById.get(vimeoId) ?? '';
  }

  private startVimeoIdValidation(): void {
    this.vimeoIdValidationSubscription = this.vimeoIdToValidate
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        mergeMap(vimeoId => this.validateVimeoIdUsage(vimeoId))
      )
      .subscribe();
  }

  private validateVimeoIdUsage(vimeoId: string): Observable<string | null> {
    const packageId: number = this.createdPackageId ?? this.editPackageId ?? 0;

    return this.packagesPersoService.validateVimeoId(vimeoId, packageId)
      .pipe(
        map(result => result.isUsable ? null : result.errorMessage),
        tap(errorMessage => this.vimeoIdErrorsById.set(vimeoId, errorMessage)),
        catchError(() => of(null))
      );
  }

  private checkVimeoIdsBeforeSave(): Observable<string | null> {
    const vimeoIds = this.getDistinctVimeoIds();
    const vimeoIdsToCheck = vimeoIds.filter(vimeoId => !this.vimeoIdErrorsById.has(vimeoId));

    if (vimeoIdsToCheck.length === 0) {
      return of(this.getFirstVimeoIdError(vimeoIds));
    }

    const validations = vimeoIdsToCheck.map(vimeoId => this.validateVimeoIdUsage(vimeoId));

    return forkJoin(validations).pipe(map(() => this.getFirstVimeoIdError(vimeoIds)));
  }

  private getDistinctVimeoIds(): string[] {
    const vimeoIds = (this.package.videos ?? [])
      .map(video => video.vimeoId?.trim())
      .filter((vimeoId): vimeoId is string => !!vimeoId);

    return [...new Set(vimeoIds)];
  }

  private getFirstVimeoIdError(vimeoIds: string[]): string | null {
    for (const vimeoId of vimeoIds) {
      const errorMessage = this.vimeoIdErrorsById.get(vimeoId);

      if (errorMessage) {
        return errorMessage;
      }
    }

    return null;
  }

  onLanguageVideoFileChange(language: LanguageCode, file: File | null): void {
    this.removeLanguageFromVideos(language);
    if (file) {
      this.uploadVideoFileForLanguage(language, file);
    }
    this.package.videos = [...this.uploadedVideos];
  }

  private uploadVideoFileForLanguage(language: LanguageCode, file: File): void {
    const videoEntry: VideoAssociation = {
      id: Date.now(),
      videoFile: file,
      vimeoId: undefined,
      languages: [language]
    };
    this.uploadedVideos.push(videoEntry);
    this.package.videos = [...this.uploadedVideos];

    this.pendingVideoUploads++;
    this.isVideoUploading = true;
    this.videoUploadingByLanguage[language] = true;
    this.videoUploadProgressByLanguage[language] = 0;
    this.videoFileError = '';

    const utilisateurId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;

    this.packagesPersoService.initierUploadVideo(file.name, file.size, utilisateurId).subscribe({
      next: (lienUpload) => {
        const upload = new tus.Upload(file, {
          uploadUrl: lienUpload.lienUpload,
          chunkSize: 50 * 1024 * 1024,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          onProgress: (bytesUploaded: number, bytesTotal: number) => {
            this.videoUploadProgressByLanguage[language] = Math.round(bytesUploaded / bytesTotal * 100);
          },
          onSuccess: () => {
            videoEntry.vimeoId = lienUpload.vimeoId;
            this.package.videos = [...this.uploadedVideos];
            this.finishVideoUpload(language);
            this.pollVideoStatus(lienUpload.vimeoId);
          },
          onError: (error: Error) => {
            this.removeVideoEntry(videoEntry);
            this.videoFileError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.VIDEO_UPLOAD_ERROR');
            this.finishVideoUpload(language);
            console.error('TUS upload error:', error);
          }
        });
        upload.start();
      },
      error: (err) => {
        this.removeVideoEntry(videoEntry);
        this.videoFileError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.VIDEO_UPLOAD_ERROR');
        this.finishVideoUpload(language);
        console.error('Error initiating upload:', err);
      }
    });
  }

  private removeVideoEntry(videoEntry: VideoAssociation): void {
    this.uploadedVideos = this.uploadedVideos.filter(v => v !== videoEntry);
    this.package.videos = [...this.uploadedVideos];
  }

  private finishVideoUpload(language: LanguageCode): void {
    this.pendingVideoUploads = Math.max(0, this.pendingVideoUploads - 1);
    this.isVideoUploading = this.pendingVideoUploads > 0;
    this.videoUploadingByLanguage[language] = false;
    this.videoUploadProgressByLanguage[language] = 0;
  }

  private pollVideoStatus(vimeoId: string | null | undefined): void {
    if (!vimeoId || this.videoStatusPolls.has(vimeoId)) {
      return;
    }

    if (!this.videoStatuses.has(vimeoId)) {
      this.videoStatuses.set(vimeoId, 'processing');
    }

    const initialDelayMs = 3000;
    const maxDelayMs = 30000;
    const deadline = Date.now() + 20 * 60 * 1000;

    const scheduleNext = (delayMs: number): void => {
      const subscription = timer(delayMs)
        .pipe(switchMap(() => this.packagesPersoService.getVideoStatus(vimeoId)))
        .subscribe({
          next: result => {
            this.videoStatuses.set(vimeoId, result.status);

            if (result.status === 'processing' && Date.now() < deadline) {
              scheduleNext(delayMs === 0 ? initialDelayMs : Math.min(delayMs * 1.5, maxDelayMs));
            } else {
              this.videoStatusPolls.delete(vimeoId);
            }
          },
          error: () => {
            if (Date.now() < deadline) {
              scheduleNext(delayMs === 0 ? initialDelayMs : Math.min(delayMs * 1.5, maxDelayMs));
            } else {
              this.videoStatusPolls.delete(vimeoId);
            }
          }
        });

      this.videoStatusPolls.set(vimeoId, subscription);
    };

    scheduleNext(0);
  }

  isVideoUploadingForLanguage(language: LanguageCode): boolean {
    return this.videoUploadingByLanguage[language] ?? false;
  }

  getVideoUploadProgress(language: LanguageCode): number {
    return this.videoUploadProgressByLanguage[language] ?? 0;
  }

  private removeLanguageFromVideos(language: LanguageCode): void {
    this.uploadedVideos = this.uploadedVideos
      .map(v => v.languages.includes(language)
        ? { ...v, languages: v.languages.filter(l => l !== language) }
        : v)
      .filter(v => v.languages.length > 0);
  }


  getSelectedLanguagesForVideoArray(): LanguageCode[] {
    return Object.entries(this.selectedLanguagesForVideo)
      .filter(([_, selected]) => selected)
      .map(([key]) => key as LanguageCode);
  }

  allSelectedLanguagesHaveVideos(): boolean {
    return Object.entries(this.selectedLanguages)
      .filter(([_, selected]) => selected)
      .every(([lang]) => this.hasVideoForLanguage(lang as LanguageCode));
  }

  /** Soumet le package déjà enregistré : passe Actif à 1 dans ScormPackages, sans refaire la création. */
  onSoumettre(): void {
    if (this.isSubmitDisabled() || this.createdPackageId === null) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationTogglePackageDialogComponent, {
      data: {
        packageId: this.createdPackageId,
        packageName: this.getLocalizedPackageName(),
        currentStatus: false
      },
      width: '500px',
      minHeight: '200px'
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.statutEnRevision = true;
        this.goBack();
      }
    });
  }

  private getLocalizedPackageName(): string {
    const currentLang = this.translate.currentLang || 'fr';
    let name = '';
    switch (currentLang) {
      case 'en':
        name = this.package.nomEN;
        break;
      case 'es':
        name = this.package.nomES;
        break;
      default:
        name = this.package.nomFR;
        break;
    }
    if (name?.trim()) return name;
    if (this.package.nomFR?.trim()) return this.package.nomFR;
    if (this.package.nomEN?.trim()) return this.package.nomEN;
    if (this.package.nomES?.trim()) return this.package.nomES;
    return this.nomPackage ?? '';
  }

  get estLimiteDemoContenusAtteinte(): boolean {
    return this.statutLimiteDemo?.limiteContenusAtteinte === true;
  }

  get estTypeContenuDejaUtilise(): boolean {
    return this.statutLimiteDemo?.typeContenuDejaUtilise === true;
  }

  get estActivationBloqueeDemo(): boolean {
    return this.estLimiteDemoContenusAtteinte || this.estTypeContenuDejaUtilise;
  }

  private chargerStatutLimiteDemo(): void {
    if (this.createdPackageId === null) {
      this.statutLimiteDemo = null;

      return;
    }

    this.packagesPersoService.obtenirStatutLimiteDemo(this.createdPackageId)
      .subscribe({
        next: statut => this.statutLimiteDemo = statut,
        error: () => this.statutLimiteDemo = null
      });
  }

  /** Le bouton Soumettre n'est actif qu'une fois le contenu enregistré (package créé, encore inactif). */
  isSubmitDisabled(): boolean {
    return this.createdPackageId === null ||
      this.estEnCoursDeEnregistrement ||
      !this.hasAnyContent() ||
      !this.allVideosReady() ||
      this.isTitreManquant() ||
      this.isPourcentageSuccesManquant() ||
      this.hasQuestionSansTexte() ||
      this.hasQuestionAvecReponsesInsuffisantes() ||
      this.hasQuestionSansBonneReponse() ||
      this.estActivationBloqueeDemo;
  }

  isTitreManquant(): boolean {
    return this.getSelectedLanguagesArray().some(language => !this.getPackageNameForLanguage(language)?.trim());
  }

  isPourcentageSuccesManquant(): boolean {
    if (this.package.questions.length === 0) {
      return false;
    }

    const pourcentage = this.package.pourcentageSucces;

    return pourcentage === undefined || pourcentage === null;
  }

  hasQuestionSansTexte(): boolean {
    const languages = this.getSelectedLanguagesArray();

    return this.package.questions.some(question =>
      languages.some(language => !this.getQuestionTextForLanguage(question, language)?.trim())
    );
  }

  hasQuestionAvecReponsesInsuffisantes(): boolean {
    return this.package.questions.some(question => {
      if (question.type === QuestionTypeEnum.FillIn) {
        return false;
      }

      return question.answers.filter(answer => this.hasAnswerText(answer)).length < this.minAnswersPerQuestion;
    });
  }

  hasQuestionSansBonneReponse(): boolean {
    return this.package.questions.some(question => {
      const bonnesReponses = question.answers.filter(answer => answer.isCorrect && this.hasAnswerText(answer));

      if (question.type === QuestionTypeEnum.MultipleChoice) {
        return bonnesReponses.length < this.minCorrectAnswersForMultipleChoice;
      }

      return bonnesReponses.length !== 1;
    });
  }

  private getPackageNameForLanguage(language: LanguageCode): string {
    switch (language) {
      case 'EN': return this.package.nomEN;
      case 'ES': return this.package.nomES;
      default: return this.package.nomFR;
    }
  }

  private getQuestionTextForLanguage(question: QuestionModel, language: LanguageCode): string {
    switch (language) {
      case 'EN': return question.textEN;
      case 'ES': return question.textES;
      default: return question.textFR;
    }
  }

  /** Un PDF ou une vidéo est exigé pour la soumission, mais jamais pour l'enregistrement d'un brouillon. */
  hasAnyContent(): boolean {
    return this.package.contentType === 'video'
      ? this.hasVideoContent()
      : this.hasPdfContent();
  }

  savePackage(): void {
    if (this.isSaveDisabled()) {
      return;
    }

    this.estEnCoursDeEnregistrement = true;

    this.applyDefaultValues();

    if (this.package.contentType === 'pdf') {
      this.uploadedVideos = [];
      this.package.videos = [];
      this.currentVimeoId = null;
      this.package.subtitleFileFR = null;
      this.package.subtitleFileEN = null;
      this.package.subtitleFileES = null;
    } else {
      this.package.pdfFileFR = null;
      this.package.pdfFileEN = null;
      this.package.pdfFileES = null;
      this.existingPdfByLanguage = { FR: false, EN: false, ES: false };
    }

    const globalAnswersAreRandom: boolean = this.package.quizInfoGenerale?.isAnswersAreRandom ?? false;

    this.package.questions.forEach(question => {
      question.answersAreRandom = globalAnswersAreRandom;
      question.answers = question.answers.filter(answer => this.hasAnswerText(answer));
      this.setQuestionImage(question, this.getQuestionImage(question));
      if (!this.selectedLanguages.FR) question.imageFileNameFR = null;
      if (!this.selectedLanguages.EN) question.imageFileNameEN = null;
      if (!this.selectedLanguages.ES) question.imageFileNameES = null;
    });

    if (this.estPourToutGroupe) {
      this.package.groupeId = this.groupeId;
      this.package.entiteIds = [];
    } else {
      this.package.groupeId = undefined;
      this.package.entiteIds = this.entitesSelectionnees.map(e => e.id);
    }

    this.package.quizConclusions = [this.failConclusion, this.passConclusion];

    this.package.nomPackage = this.nomPackage;
    this.package.scormVersion = this.scormVersion;

    this.checkVimeoIdsBeforeSave().subscribe(vimeoIdErrorMessage => {
      if (vimeoIdErrorMessage) {
        this.estEnCoursDeEnregistrement = false;
        this.snackBar.open(vimeoIdErrorMessage, 'Fermer', { duration: 10000 });

        return;
      }

      if (this.createdPackageId !== null) {
        this.updateExistingPackage(this.createdPackageId);

        return;
      }

      this.createNewPackage();
    });
  }

  private createNewPackage(): void {
    this.packagesPersoService.createPackage(this.package)
      .subscribe({
        next: (response: CreatePackageResponse) => {
          this.estEnCoursDeEnregistrement = false;
          // Le package est créé inactif (brouillon); on reste sur la page pour permettre la soumission.
          this.createdPackageId = response?.scormPackageId ?? null;
          if (this.createdPackageId !== null) {
            this.editPackageId = this.createdPackageId;
            this.location.replaceState(`/scorm-perso-managment/create/${this.createdPackageId}`);
          }
          this.discardDraft();
          this.capturePristineState();
          this.snackBar.open(this.buildSaveSuccessMessage('PACKAGES_PERSO_MANAGEMENT.CREATION_SUCCESS'), 'Fermer', { duration: 8000 });
          this.chargerStatutLimiteDemo();
        },
        error: err => {
          this.estEnCoursDeEnregistrement = false;

          if (err?.status === 422 && isDemoLimitErrorCode(err.error?.codeErreur)) {
            return;
          }

          const validationMessages: string[] | undefined = err?.error?.validationMessages;
          const serverMessage = this.extractServerErrorMessage(err);
          if (validationMessages && validationMessages.length > 0) {
            const message = `${this.translate.instant('PACKAGES_PERSO_MANAGEMENT.MANIFEST_VALIDATION_FAILED')} : ${validationMessages.join(' • ')}`;
            this.snackBar.open(message, 'Fermer', { duration: 10000 });
          } else if (serverMessage) {
            this.snackBar.open(serverMessage, 'Fermer', { duration: 10000 });
          } else {
            this.snackBar.open(this.translate.instant('PACKAGES_PERSO_MANAGEMENT.CREATION_ERROR'), 'Fermer', { duration: 5000 });
          }
          console.error('Erreur:', err);
        }
      });
  }

  private updateExistingPackage(packageId: number): void {
    this.package.keptPdfLanguages = this.package.contentType === 'pdf'
      ? this.getSelectedLanguagesArray().filter(lang =>
          this.existingPdfByLanguage[lang] && !this.getPdfFileForLanguage(lang))
      : [];

    this.packagesPersoService.updatePackage(packageId, this.package)
      .subscribe({
        next: () => {
          this.estEnCoursDeEnregistrement = false;
          this.discardDraft();
          this.capturePristineState();
          this.snackBar.open(this.buildSaveSuccessMessage('PACKAGES_PERSO_MANAGEMENT.UPDATE_SUCCESS'), 'Fermer', { duration: 8000 });
          this.chargerStatutLimiteDemo();
        },
        error: err => {
          this.estEnCoursDeEnregistrement = false;

          if (err?.status === 422 && isDemoLimitErrorCode(err.error?.codeErreur)) {
            return;
          }

          const validationMessages: string[] | undefined = err?.error?.validationMessages;
          const serverMessage = this.extractServerErrorMessage(err);
          if (validationMessages && validationMessages.length > 0) {
            const message = `${this.translate.instant('PACKAGES_PERSO_MANAGEMENT.MANIFEST_VALIDATION_FAILED')} : ${validationMessages.join(' • ')}`;
            this.snackBar.open(message, 'Fermer', { duration: 10000 });
          } else if (serverMessage) {
            this.snackBar.open(serverMessage, 'Fermer', { duration: 10000 });
          } else {
            this.snackBar.open(this.translate.instant('PACKAGES_PERSO_MANAGEMENT.UPDATE_ERROR'), 'Fermer', { duration: 5000 });
          }
          console.error('Erreur:', err);
        }
      });
  }

  private extractServerErrorMessage(err: unknown): string | null {
    const error = (err as { error?: unknown })?.error;

    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    return null;
  }

  private buildSaveSuccessMessage(baseKey: string): string {
    let message = this.translate.instant(baseKey);

    if (this.hasProcessingVideos()) {
      message += ' ' + this.translate.instant('PACKAGES_PERSO_MANAGEMENT.VIDEO_PROCESSING_NOTICE');
    }

    if (this.package.subtitleFileFR || this.package.subtitleFileEN || this.package.subtitleFileES) {
      message += ' ' + this.translate.instant('PACKAGES_PERSO_MANAGEMENT.SUBTITLE_PROCESSING_NOTICE');
    }

    return message;
  }

  private getPdfFileForLanguage(language: LanguageCode): File | null {
    switch (language) {
      case 'EN': return this.package.pdfFileEN;
      case 'ES': return this.package.pdfFileES;
      default: return this.package.pdfFileFR;
    }
  }

  getExistingPdfFileName(language: LanguageCode): string | null {
    return this.existingPdfByLanguage[language] ? `content_${language}.pdf` : null;
  }

  onExistingPdfRemoved(language: LanguageCode): void {
    this.existingPdfByLanguage[language] = false;
  }

  getExistingQuestionImageName(question: QuestionModel): string | null {
    return question.imageFileNameFR ?? question.imageFileNameEN ?? question.imageFileNameES ?? null;
  }

  /** Aucun champ n'est requis : seules les opérations en cours empêchent l'enregistrement. */
  isSaveDisabled(): boolean {
    return this.estEnCoursDeEnregistrement || this.isVideoUploading;
  }

  private applyDefaultValues(): void {
    this.applyDefaultPackageName();
    this.applyDefaultLanguages();
    this.applyDefaultDuration();
    this.applyDefaultCertificateExpiration();
    this.applyDefaultEntityAssignment();

    if (this.addQuestionnaire) {
      this.applyDefaultQuizValues();
    }
  }

  private applyDefaultPackageName(): void {
    if (!this.nomPackage?.trim()) {
      this.nomPackage = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.CONTENU_PERSONNALISE_TITLE');
    }

    if (!this.scormVersion?.trim()) {
      this.scormVersion = this.defaultScormVersion;
    }
  }

  private applyDefaultLanguages(): void {
    if (!this.hasSelectedLanguage()) {
      this.selectedLanguages.FR = true;
    }
  }

  private applyDefaultDuration(): void {
    const duration = this.package.dureeEnMinutes;

    if (duration === undefined || duration === null || duration < this.minDurationInMinutes) {
      this.package.dureeEnMinutes = this.defaultDurationInMinutes;

      return;
    }

    if (duration > this.maxDurationInMinutes) {
      this.package.dureeEnMinutes = this.maxDurationInMinutes;
    }
  }

  private applyDefaultCertificateExpiration(): void {
    if (!this.package.donneCertificat) {
      return;
    }

    const expiration = this.package.expirationCertificatEnMois;

    if (expiration === undefined || expiration === null) {
      return;
    }

    if (expiration < this.minCertificateExpirationInMonths) {
      this.package.expirationCertificatEnMois = this.minCertificateExpirationInMonths;
    } else if (expiration > this.maxCertificateExpirationInMonths) {
      this.package.expirationCertificatEnMois = this.maxCertificateExpirationInMonths;
    }
  }

  private applyDefaultEntityAssignment(): void {
    if (this.estPourToutGroupe || this.entitesSelectionnees.length > 0) {
      return;
    }

    if (this.estPiloteGroupe && this.groupeId !== undefined) {
      this.estPourToutGroupe = true;

      return;
    }

    const entiteIdCourante = this.utilisateurService.entiteUtilisateurSelectionnee?.entiteId;
    const entiteCourante = this.entitesAccessibles.find(entite => entite.id === entiteIdCourante);
    const entiteParDefaut = entiteCourante ?? this.entitesAccessibles[0];

    if (entiteParDefaut) {
      this.entitesSelectionnees = [entiteParDefaut];
      this.mettreAJourEntitesDisponibles();
    }
  }

  private applyDefaultQuizValues(): void {
    this.clampPourcentageSucces();

    this.applyDefaultConclusionTitles(this.failConclusion, 'echecTitre');
    this.applyDefaultConclusionTitles(this.passConclusion, 'reussiteTitre');
  }

  private applyDefaultConclusionTitles(
    conclusion: QuizConclusionModel,
    field: QuizPlaceholderField
  ): void {
    if (this.selectedLanguages.FR && !conclusion.titreFR?.trim()) {
      conclusion.titreFR = this.getQuizPlaceholder(field, 'FR');
    }
    if (this.selectedLanguages.EN && !conclusion.titreEN?.trim()) {
      conclusion.titreEN = this.getQuizPlaceholder(field, 'EN');
    }
    if (this.selectedLanguages.ES && !conclusion.titreES?.trim()) {
      conclusion.titreES = this.getQuizPlaceholder(field, 'ES');
    }
  }

  getSelectedLanguagesArray(): LanguageCode[] {
    return Object.entries(this.selectedLanguages)
      .filter(([_, value]) => value)
      .map(([key]) => key as LanguageCode);
  }

  hasSelectedLanguage(): boolean {
    return Object.values(this.selectedLanguages).some(value => value);
  }

  get activeLanguagesCount(): number {
    return Object.values(this.selectedLanguages).filter(v => v).length;
  }

  addLanguage(lang: string): void {
    if (lang === 'FR' || lang === 'EN' || lang === 'ES') {
      this.selectedLanguages[lang] = true;
      this.contentEditingLanguage = lang;
    }
  }

  removeLanguage(lang: 'FR' | 'EN' | 'ES'): void {
    this.selectedLanguages[lang] = false;
    this.onLanguageChange(lang);
  }

  onLanguageChange(language: 'FR' | 'EN' | 'ES'): void {
    if (!this.selectedLanguages[language]) {
      switch (language) {
        case 'FR':
          this.package.nomFR = '';
          this.package.descriptionFR = '';
          this.package.pdfFileFR = null;
          this.package.subtitleFileFR = null;
          break;
        case 'EN':
          this.package.nomEN = '';
          this.package.descriptionEN = '';
          this.package.pdfFileEN = null;
          this.package.subtitleFileEN = null;
          break;
        case 'ES':
          this.package.nomES = '';
          this.package.descriptionES = '';
          this.package.pdfFileES = null;
          this.package.subtitleFileES = null;
          break;
      }

      this.existingPdfByLanguage[language] = false;
      this.package.questions.forEach(question => {
        switch (language) {
          case 'FR': question.imageFileNameFR = null; break;
          case 'EN': question.imageFileNameEN = null; break;
          case 'ES': question.imageFileNameES = null; break;
        }
      });

      this.uploadedVideos = this.uploadedVideos.map(video => {
        return {
          ...video,
          languages: video.languages.filter(lang => lang !== language)
        };
      });

      this.uploadedVideos = this.uploadedVideos.filter(video => video.languages.length > 0);
      this.package.videos = [...this.uploadedVideos];
    }
  }

  hasPdfContent(): boolean {
    return !!this.package.pdfFileFR || !!this.package.pdfFileEN || !!this.package.pdfFileES ||
      this.existingPdfByLanguage.FR || this.existingPdfByLanguage.EN || this.existingPdfByLanguage.ES;
  }

  hasVideoContent(): boolean {
    return this.uploadedVideos.length > 0 || this.isVideoUploading;
  }

  isContentTypeSwitchBlocked(targetType: 'pdf' | 'video'): boolean {
    if (this.package.contentType === targetType) {
      return false;
    }

    if (this.estTypeContenuDejaUtilise) {
      return false;
    }

    return this.package.contentType === 'pdf' ? this.hasPdfContent() : this.hasVideoContent();
  }

  obtenirTitreTypeContenu(targetType: 'pdf' | 'video'): string {
    if (this.isContentTypeSwitchBlocked(targetType)) {
      return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.CONTENT_TYPE_LOCKED');
    }

    if (this.package.contentType !== targetType && this.estTypeContenuDejaUtilise) {
      return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.CONTENT_TYPE_DEMO_SWITCH');
    }

    return '';
  }

  onContentTypeChange(type: 'pdf' | 'video' | 'multi-chapitres'): void {
    if (type === 'multi-chapitres') {
      this.package.contentType = type;
      this.package.readingConfirmationRequired = false;
      return;
    }

    if (this.package.contentType === type || this.isContentTypeSwitchBlocked(type)) {
      return;
    }

    this.package.contentType = type;

    if (type === 'pdf') {
      this.uploadedVideos = [];
      this.package.videos = [];
      this.currentVimeoId = null;
      this.package.subtitleFileFR = null;
      this.package.subtitleFileEN = null;
      this.package.subtitleFileES = null;
    } else {
      this.package.pdfFileFR = null;
      this.package.pdfFileEN = null;
      this.package.pdfFileES = null;
      this.existingPdfByLanguage = { FR: false, EN: false, ES: false };
      this.package.readingConfirmationRequired = false;
    }
  }

  onDragOverVideo(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingVideo = true;
  }

  onDragLeaveVideo(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingVideo = false;
  }

  onDropVideo(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingVideo = false;
    this.videoFileError = '';

    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    const file = files[0];
    const fileType = file.type;

    if (fileType.startsWith('video/')) {
      this.demarrerUploadTus(file);
    } else {
      this.videoFileError = 'Please upload a video file';
      setTimeout(() => this.videoFileError = '', 3000);
    }
  }

  mettreAJourEntitesDisponibles(): void {
    this.entitesDisponibles = this.entitesAccessibles.filter(
      entite => !this.entitesSelectionnees.some(selected => selected.id === entite.id)
    );
    this.entiteIdSelectionnee = -1;
  }

  nomEntite(id: number | null): string {
    return this.entitesAccessibles.find(e => e.id === id)?.nom ?? '';
  }

  onEntiteSelected(entiteId: number): void {
    // « Ensemble du groupe » (-1) : attribue la formation à tout le groupe.
    if (entiteId === -1) {
      if (this.estPiloteGroupe) {
        this.estPourToutGroupe = true;
        this.entitesSelectionnees = [];
        this.mettreAJourEntitesDisponibles();
      }
      return;
    }

    const entiteSelectionnee = this.entitesDisponibles.find(e => e.id === entiteId);
    if (entiteSelectionnee) {
      this.estPourToutGroupe = false;
      this.entitesSelectionnees.push(entiteSelectionnee);
      this.mettreAJourEntitesDisponibles();
    }
  }

  removeSelectedEntity(id: number): void {
    if (this.entitesAccessibles.length === 1 && this.entitesSelectionnees.length === 1) {
      return;
    }

    this.entitesSelectionnees = this.entitesSelectionnees.filter(entite => entite.id !== id);

    // Un pilote de groupe qui retire sa dernière entité revient à « Ensemble du groupe ».
    if (this.estPiloteGroupe && this.entitesSelectionnees.length === 0) {
      this.estPourToutGroupe = true;
    }

    this.mettreAJourEntitesDisponibles();
  }

  onThumbnailSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.currentThumbnail = input.files[0];
      this.package.thumbnail = input.files[0];
    }
  }

  resetThumbnail(): void {
    this.currentThumbnail = null;
    this.package.thumbnail = null;
    this.package.existingThumbnailFileName = null;
  }

  onDragOverThumbnail(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingThumbnail = true;
  }

  onDragLeaveThumbnail(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingThumbnail = false;

  }

  onDurationInput(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.value.length > 3) {
      input.value = input.value.slice(0, 3);
      this.package.dureeEnMinutes = input.value === '' ? undefined : Number(input.value);
    }
  }

  onAttachedDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.ajouterAttachedDocuments(Array.from(input.files));
    }
    input.value = '';
  }

  onDragOverAttachedDocument(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingDocument = true;
  }

  onDragLeaveAttachedDocument(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingDocument = false;
  }

  onDropAttachedDocument(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingDocument = false;

    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    this.ajouterAttachedDocuments(Array.from(files));
  }

  retirerAttachedDocument(index: number): void {
    this.package.attachedDocuments.splice(index, 1);
  }

  formaterTailleDocument(bytes: number): string {
    if (bytes < 1024) return `${bytes} o`;
    const ko = bytes / 1024;
    if (ko < 1024) return `${ko.toFixed(1)} Ko`;
    const mo = ko / 1024;
    return `${mo.toFixed(1)} Mo`;
  }

  private ajouterAttachedDocuments(files: File[]): void {
    this.attachedDocumentError = '';

    const remainingSlots = this.attachedDocumentMaxCount - this.package.attachedDocuments.length;
    if (remainingSlots <= 0) {
      this.attachedDocumentError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.ATTACHED_DOCUMENTS_MAX_FICHIERS', { count: this.attachedDocumentMaxCount });
      setTimeout(() => (this.attachedDocumentError = ''), 4000);

      return;
    }

    const acceptes: AttachedDocument[] = [];
    for (const file of files) {
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!this.attachedDocumentAllowedExtensions.includes(extension)) {
        this.attachedDocumentError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.ATTACHED_DOCUMENTS_FORMAT_INVALIDE');
        continue;
      }
      if (file.size > this.attachedDocumentMaxSizeBytes) {
        this.attachedDocumentError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.ATTACHED_DOCUMENTS_TAILLE_INVALIDE');
        continue;
      }
      acceptes.push({ id: Date.now() + acceptes.length, file, titre: '' });
    }

    if (acceptes.length > remainingSlots) {
      acceptes.length = remainingSlots;
      this.attachedDocumentError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.ATTACHED_DOCUMENTS_MAX_FICHIERS', { count: this.attachedDocumentMaxCount });
    }

    if (acceptes.length) {
      this.package.attachedDocuments = [...this.package.attachedDocuments, ...acceptes];
    }
    if (this.attachedDocumentError) {
      setTimeout(() => (this.attachedDocumentError = ''), 4000);
    }
  }

  onDropThumbnail(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDraggingThumbnail = false;

    this.thumbnailFileError = '';

    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    const file = files[0];

    if (file.type.startsWith('image/')) {
      this.currentThumbnail = file;
      this.package.thumbnail = file;
    } else {
      this.thumbnailFileError = this.translate.instant('PACKAGES_PERSO_MANAGEMENT.IMAGE_FORMAT_INVALIDE');
      setTimeout(() => this.thumbnailFileError = '', 3000);
    }
  }

  validateVimeoId(id: string): boolean {
    return /^\d+$/.test(id);
  }

  onQuestionImageSelected(event: Event, question: QuestionModel): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.setQuestionImage(question, input.files[0]);
    }
  }

  removeQuestionImage(question: QuestionModel): void {
    this.setQuestionImage(question, null);
    question.imageFileNameFR = null;
    question.imageFileNameEN = null;
    question.imageFileNameES = null;
  }

  getQuestionImage(question: QuestionModel): File | null {
    return question.imageFR ?? question.imageEN ?? question.imageES;
  }

  private setQuestionImage(question: QuestionModel, file: File | null): void {
    question.imageFR = this.selectedLanguages.FR ? file : null;
    question.imageEN = this.selectedLanguages.EN ? file : null;
    question.imageES = this.selectedLanguages.ES ? file : null;
  }

  /**
   * Garantit l'existence des deux conclusions « échec » et « réussite ».
   * Appelée à l'initialisation et utilisée par les getters de liaison.
   */
  private getOrCreateConclusion(passing: boolean): QuizConclusionModel {
    if (!this.package.quizConclusions) {
      this.package.quizConclusions = [];
    }
    let conclusion = this.package.quizConclusions.find(c => this.isConclusionPassing(c) === passing);
    if (!conclusion) {
      conclusion = {
        id: Date.now() + (passing ? 1 : 0),
        scoreMax: passing ? 100 : (this.package.pourcentageSucces ?? null),
        isPassing: passing,
        titreFR: '',
        titreEN: '',
        titreES: '',
        sousTitreFR: '',
        sousTitreEN: '',
        sousTitreES: '',
      };
      if (passing) {
        this.package.quizConclusions.push(conclusion);
      } else {
        this.package.quizConclusions.unshift(conclusion);
      }
    }
    return conclusion;
  }

  private ensureConclusionPair(): void {
    this.getOrCreateConclusion(false);
    this.getOrCreateConclusion(true);
  }

  /** Conclusion affichée lorsque l'apprenant est en dessous du pourcentage de réussite. */
  get failConclusion(): QuizConclusionModel {
    return this.getOrCreateConclusion(false);
  }

  /** Conclusion affichée lorsque l'apprenant est au-dessus (ou égal) du pourcentage de réussite. */
  get passConclusion(): QuizConclusionModel {
    return this.getOrCreateConclusion(true);
  }

  /** Détermine si un palier de conclusion correspond à une réussite (au-dessus du seuil). */
  isConclusionPassing(palier: QuizConclusionModel): boolean {
    if (typeof palier.isPassing === 'boolean') {
      return palier.isPassing;
    }
    const pourcentage = this.package.pourcentageSucces;
    if (palier.scoreMax !== null && palier.scoreMax !== undefined &&
        pourcentage !== null && pourcentage !== undefined) {
      return palier.scoreMax > pourcentage;
    }
    return false;
  }

  clampPourcentageSucces(): void {
    if (this.package.pourcentageSucces !== null && this.package.pourcentageSucces !== undefined) {
      if (this.package.pourcentageSucces < 0) this.package.pourcentageSucces = 0;
      else if (this.package.pourcentageSucces > 100) this.package.pourcentageSucces = 100;
    }
    // Le palier « échec » couvre les scores en dessous du pourcentage de réussite.
    const fail = this.package.quizConclusions?.find(c => c.isPassing === false);
    if (fail) {
      fail.scoreMax = this.package.pourcentageSucces ?? null;
    }
  }

  trackByQuestionId(_: number, q: QuestionModel): number {
    return q.id;
  }

  trackByAnswerId(_: number, a: AnswerModel): number {
    return a.id;
  }

  goBack(): void {
    this.location.back();
  }

  // Aperçu du contenu personnalisé désactivé temporairement (à réactiver plus tard)
  // onAfficherApercu(): void {
  //   this.apercuVisible = !this.apercuVisible;
  // }
}
